import axios from 'axios';
import template from 'url-template';
import moment from 'moment';
import { get, groupBy, isEmpty, isNil, keys, pickBy, orderBy, toNumber, cloneDeep } from 'lodash';
import { getRuleFvoSensorFilterName } from '../helpers/rules';

import {
    CLEAR_DASHBOARD_DATA,
    CLEAR_DASHBOARD_CLIENT_DATA,
    CLEAR_DASHBOARD_SITE_DATA,
    CREATE_CUSTOMER_SUPPORT_REPORT,
    CREATE_INSPECTION_REPORT,
    FETCH_CLIENT_ENERGY_DATA,
    FETCH_CLIENT_EQUIPMENT_DATA,
    FETCH_CLIENTS_DATA,
    FETCH_CLIENT_SITES_DATA,
    FETCH_CLIENT_SYSTEMS_DATA,
    FETCH_CONFIG_ENTITIES,
    FETCH_COMPANY_ENERGY_DATA,
    FETCH_COMPANY_KPI_DATA,
    FETCH_EQUIPMENT_DATA,
    FETCH_EQUIPMENT_KPI_DATA,
    FETCH_ENERGY_INTEGRATIONS,
    FETCH_ENERGY_KPI_DATA,
    FETCH_HEALTHSCORE_RAW_DATA,
    FETCH_RULE_SCORE_KPIS,
    FETCH_SENSOR_CALCULATIONS,
    FETCH_FORMULA_LIST,
    FETCH_COMPANY_FORMULA_LIST,
    FETCH_SITE_EQUIPMENT_DATA,
    FETCH_SITES_DATA,
    FETCH_SITE_EQUIPMENT_KPI_DATA,
    FETCH_SITE_SYSTEM_KPI_DATA,
    FETCH_SITE_SYSTEMS_DATA,
    FETCH_SYSTEM_EQUIPMENT_DATA,
    FETCH_SYSTEM_KPI_DATA,
    FETCH_SYSTEMS_DATA,
    FETCH_USER_LIST,
    FETCH_USER_ENTITIES,
    SET_CLIENT_FILTERS,
    SET_CLIENT_TAB_INDEX,
    SET_COMPANY_TAB_INDEX,
    SET_DASHBOARD_TIMEFRAME_END,
    SET_DASHBOARD_TIMEFRAME_START,
    SET_DASHBOARD_TIME_PERIOD,
    SET_CLONED_FORMULA,
    SET_DASHBOARD_END_TIMESTAMP,
    SET_DASHBOARD_ISCURRENT_TIMESTAMP,
    SET_DASHBOARD_CURRENT_TIMESTAMP,
    SET_DASHBOARD_START_TIMESTAMP,
    SET_DESIRED_INTERVAL,
    SET_EQUIPMENT_FILTERS,
    SET_EQUIPMENT_TAB_INDEX,
    SET_EMAILS,
    SET_FORMULA_GRID_COLUMNS,
    SET_HEALTH_MONITOR_FILTERS,
    SET_RULE_SCORING_CONFIGURATION,
    SET_SITE_FILTERS,
    SET_SITE_TAB_INDEX,
    SET_SELECTED_RULE,
    SET_SELECTED_RULE_FROM_FAULTS_PAGE,
    SET_SELECTED_RULE_SENSORS,
    SET_SELECTED_SENSOR,
    SET_SELECTED_SENSORS,
    SET_SELECTED_WEATHER_SENSORS,
    SET_SENSOR_CONFIGURATION,
    SET_SITE_FAULT_SUMMARY,
    SET_SYSTEM_FILTERS,
    SET_SYSTEM_TAB_INDEX,
    SET_SHOW_EXPORT_MODAL,
    SET_WORKBOOK_MODE,
    FETCH_ANNOUNCEMENTS,
    FETCH_ANNOUNCEMENT_MSG,
    FETCH_REPORT_LIST,
    SET_FORMULA_DETAILS,
    SET_TEMPLATE_DETAILS,
    FETCH_SENSOR_TEMPLATE,
    SENSOR_SAVE_EVENT
} from './actionTypes';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';

import { enqueueSnackbar, handleNetworkError } from './applicationMessageActions';
import { setProgressBarLoading } from './coreActions';
import { setProcessId } from './reportingActions';
import { setBobLoading } from './bobActions';

import { companyKpiData } from '../data/companyKpiData';
import { energyKpiData } from '../data/energyKpiData';
import { userEntitiesData } from '../data/userEntitiesData';
import { announcementMsg } from '../data/announcementmsg';

const DashboardDataUrl = template.parse('/companies/{companyId}/healthdashboards{?limit,page,searchFilter,sort,tabView,clientId,siteId,equipmentId,systemId,businessTypeId,buildingTypeId,equipmentTypeId,systemTypeId,priority,export,startDate,endDate,excludeFault,ruleId}');
const DashboardTimeSeriesDataUrl = template.parse('/companies/{companyId}/score-sensordata{?limit,page,searchFilter,sort,tabView,clientId,siteId,EquipmentId,SystemId,businessTypeId,buildingTypeId,equipmentTypeId,systemTypeId,priority,from,to,export,exportType,DesiredTimeSeriesIntervalInMinutes,timediff}');
const DashboardKpiUrl = template.parse('/healthscore-kpi/{companyId}{?tabView,entityId,startDate,endDate}');
const RuleScoreKpiUrl = template.parse('kpi/{companyId}/rule-score/{entityId}{?From,To,DesiredTimeSeriesIntervalInMinutes,EntityName}');
const EnergyPrintUrl = template.parse('/companies/{companyId}/energy/integrations{?page,limit}');
const EquipmentUrl = template.parse('/sites/{siteId}/equipment{?searchFilter,limit,page,extendedData}');
const EquipmentFaultsDataExportUrl = template.parse('/companies/{companyId}/equipment/{equipmentId}/faults{?export,StartTime,EndTime}');
const EquipmentScoreUrl = template.parse('/sites/{siteId}/equipment-score{?startDate,endDate,systemIds,EquipmentType}');
const ExportedFileUrl = template.parse('/companies/{companyId}/resource{?resourceId,resourceType}');
const SensorUrl = template.parse('/sensors/{sensorId}');
const SensorIdsUrl = template.parse('/site/{siteId}/equipment/{entityId}/rule-sensors?');
const SensorCalculationUrl = template.parse('/sensorcalculations{?systemId,equipmentId,limit,page}');
const SensorCalculationCreateUrl = template.parse('/sensorcalculations');
const SensorCalculationLoadUrl = template.parse('/sensorcalculations/{id}');
const formulaListUrl = template.parse('/formulas/companies/{companyId}');
const formulaDetailsUrl = template.parse('/formulas/{formulaId}/companies/{companyId}/effectiveDate/{effectiveDate}');
const formulaCreateUrl = template.parse('/formulas');
const SystemsUrl = template.parse('/sites/{siteId}/systems{?searchFilter,limit,page,extendedData}');
const AnnouncementUrl = template.parse('/announcements{?page,limit}');
const AnnouncementUrlwithId = template.parse('/announcements/{announcementId}');
const SensordataUrl = template.parse('/sensordata{?endTimeStamp,page,pageSize,desiredInterval,export,startTimeStamp, sensorId}');
const ReportsUrl = template.parse('/worker-process-collection{?processId,processType,groupId,status,userId}');
const CreateReportUrl = template.parse('/reports/createreport');
const CreateCustomerSupportReportUrl = template.parse('/reports/customersupportreport{?companyId, userId, processGroupId, starttime, endtime}');
const ReportUrl = template.parse('/worker-process{?processId,groupId}');
const ResetPasswordUrl = template.parse(`/users/{userId}/reset/password`);
const UsersUrl = template.parse('/companies/{companyId}/users{?page,limit}');
const CreateUserUrl = template.parse('/users{?isSuperUser}');
const UpdateUserUrl = template.parse('/users/{userId}{?isSuperUser}');
const DeleteUserTypeUrl = template.parse('/usertypes/{userTypeUserId}');
const AddUserTypeUrl = template.parse('/users/{userId}/companies/{companyId}/usertypes');
const FetchUserApplicationsUrl = template.parse('/usercompanyapplications/users/{userId}/companies/{companyId}');
const CreateUserCompanyApplicationsUrl = template.parse('/usercompanyapplications');
const UpdateUserCompanyApplicationsUrl = template.parse('/usercompanyapplications/{userCompanyApplicationId}');
const FetchUserContactDetailsUrl = template.parse('/users/{userId}/usertypes{?UserType}');
const QueryParameterUrl = template.parse('/queryparameters');
const SiteFaultsUrl = template.parse('/companies/{companyId}/site/{siteId}/site-faults-summary{?startTime,endTime,sort,searchFilter,priorityFilter,currentStatusFilter,limit,page,allRecords}');
const SitesByCompanyIdUrl = template.parse('/companies/{companyId}/sites{?limit}');
const FetchUserContextUrl = template.parse('/usercontext/users/{userId}/companies/{companyId}');
const RolesByUserIdUrl = template.parse('/role/get/user/{userId}/roles');


const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const fetchDashboardData = (companyId, tabView, options = {}, types, priorities) => async (dispatch, getState) => {
    const ignoreTimestamp = ['CompanyClient', 'CompanySite', 'CompanyEquipment', 'CompanySystem', 'ClientSite', 'ClientEquipment', 'ClientSystem', 'SiteEquipment', 'SiteSystem'];
    const header = await awsHeader();
    options.tabView = tabView;

    let data = [];
    let meta = {
        errorMessage:     'Error retrieving data',
        totalRecordCount: 0
    };
    let totalRecordCount = 0;
    let totalPayload = [];

    dispatch(setBobLoading(tabView));

    dispatch(setProgressBarLoading(true));


    try {
        const getPayload = async (page = 1) => {
            options.limit = 1000;
            options.page = page;

            if (getState().dashboard.dashboardIsCurrentTimeStamp && ignoreTimestamp.includes(tabView)) {
                options.startDate = null;
                options.endDate = null;
            }

            let url = DashboardDataUrl.expand({ companyId, ...options });

            if (!isEmpty(types)) {
                switch (tabView) {
                    case 'CompanyClient':
                        types.forEach(type => {
                            url = `${url}&businessTypeId=${type}`;
                        });
                        break;
                    case 'CompanySite':
                    case 'ClientSite':
                        types.forEach(type => {
                            url = `${url}&buildingTypeId=${type}`;
                        });
                        break;
                    case 'CompanyEquipment':
                    case 'ClientEquipment':
                    case 'SiteEquipment':
                        types.forEach(type => {
                            url = `${url}&equipmentTypeId=${type}`;
                        });
                        break;
                    case 'CompanySystem':
                    case 'ClientSystem':
                    case 'SiteSystem':
                        types.forEach(type => {
                            url = `${url}&systemTypeId=${type}`;
                        });
                        break;
                    default:
                        break;
                }

            }

            if (!isEmpty(priorities)) {
                priorities.forEach(priority => {
                    url = `${url}&priority=${priority}`;
                });
            }

            const response = await k2aBaseApi.get(
                url,
                header
            );

            meta = get(response, 'data.summaryMeta', {});
            totalRecordCount = meta.totalRecordCount;

            if (!isNil(response.data.healthcheckSummaries)) {
                data = get(response, 'data.healthcheckSummaries', []);

                if (!isEmpty(data)) {
                    data.forEach(entity => {
                        const keys = Object.keys(entity).filter(item => item.toLowerCase().includes('score'));

                        keys.forEach(key => {
                            if (entity[key]) {
                                entity[key] = entity[key] * 100;
                            }
                        });

                        entity.companyId = companyId;

                        if (tabView.toLowerCase().includes('equipment')) {
                            if (entity.areas) {
                                entity.areaList = orderBy(entity.areas, ['name'], ['asc']).map(a => a.name).join(', ');
                            }
                            else {
                                entity.areaList = null;
                            }
                        }
                    });
                }

                totalPayload = [...totalPayload, ...data];
            }

            page++;

            if (data.length > 0 && totalPayload.length < totalRecordCount) {
                return await getPayload(page);
            }
            return;
        };

        await getPayload();

        if (tabView === 'CompanyClient') {
            totalPayload.forEach(item => {
                if (!isEmpty(item.customAttributes)) {
                    item.customAttributes.forEach((attr) => {
                        item[attr.customAvp.attributeName] = attr.customAvp.attributeValue;
                        item[`${attr.customAvp.attributeName}AttributeId`] = attr.customAvp.attributeId;
                        item[`${attr.customAvp.attributeName}AvpId`] = attr.customAvp.avpId;
                    });
                }
            });
        }

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setDashboardCurrentTimestamp(moment().local()));
        dispatch(setBobLoading(tabView));
        dispatch(setProgressBarLoading(false));
    }

    totalPayload = sortDashboardData(cloneDeep(totalPayload), tabView, getState().dashboard.dashboardIsCurrentTimeStamp && ignoreTimestamp.includes(tabView));

    switch (tabView) {
        case 'CompanyClient':

            dispatch({
                type:    FETCH_CLIENTS_DATA,
                payload: {
                    clients: [...totalPayload],
                    meta
                }
            });
            break;
        case 'CompanySite':

            dispatch({
                type:    FETCH_SITES_DATA,
                payload: {
                    sites: [...totalPayload],
                    meta
                }
            });
            break;
        case 'CompanyEquipment':

            dispatch({
                type:    FETCH_EQUIPMENT_DATA,
                payload: {
                    equipment: [...totalPayload],
                    meta
                }
            });
            break;
        case 'CompanySystem':

            dispatch({
                type:    FETCH_SYSTEMS_DATA,
                payload: {
                    systems: [...totalPayload],
                    meta
                }
            });
            break;
        case 'ClientSite':
            dispatch({
                type:    FETCH_CLIENT_SITES_DATA,
                payload: {
                    clientSites: [...totalPayload],
                    meta
                }
            });
            break;
        case 'ClientEquipment':
            dispatch({
                type:    FETCH_CLIENT_EQUIPMENT_DATA,
                payload: {
                    clientEquipment: [...totalPayload],
                    meta
                }
            });
            break;
        case 'ClientSystem':

            dispatch({
                type:    FETCH_CLIENT_SYSTEMS_DATA,
                payload: {
                    clientSystems: [...totalPayload],
                    meta
                }
            });
            break;
        case 'SiteEquipment':

            dispatch({
                type:    FETCH_SITE_EQUIPMENT_DATA,
                payload: {
                    siteEquipment: [...totalPayload],
                    meta
                }
            });
            break;
        case 'SiteSystem':

            dispatch({
                type:    FETCH_SITE_SYSTEMS_DATA,
                payload: {
                    siteSystems: [...totalPayload],
                    meta
                }
            });
            break;
        case 'SystemEquipment':

            dispatch({
                type:    FETCH_SYSTEM_EQUIPMENT_DATA,
                payload: {
                    systemEquipment: [...totalPayload],
                    meta
                }
            });
            break;
        default:
            break;
    }
};

export const fetchDashboardEquipmentData = (companyId, tabView, options = {}, types, priorities) => async (dispatch, getState) => {
    const ignoreTimestamp = ['CompanyClient', 'CompanySite', 'CompanyEquipment', 'CompanySystem', 'ClientSite', 'ClientEquipment', 'SiteEquipment', 'SiteSystem'];
    const header = await awsHeader();
    options.tabView = tabView;

    let meta = {
        errorMessage:     'Error retrieving data',
        totalRecordCount: 0
    };

    let totalRecordCount = 0;
    let totalPayload = [];

    dispatch(setBobLoading(tabView));

    dispatch(setProgressBarLoading(true));

    try {
        options.page = 1;
        options.limit = 250;

        // dont pass timestamp when current is selected
        if (getState().dashboard.dashboardIsCurrentTimeStamp && ignoreTimestamp.includes(tabView)) {
            options.startDate = null;
            options.endDate = null;
        }

        let url = DashboardDataUrl.expand({ companyId, ...options });

        if (!isEmpty(types)) {
            switch (tabView) {
                case 'CompanyClient':
                    types.forEach(type => {
                        url = `${url}&businessTypeId=${type}`;
                    });
                    break;
                case 'CompanySite':
                case 'ClientSite':
                    types.forEach(type => {
                        url = `${url}&buildingTypeId=${type}`;
                    });
                    break;
                case 'CompanyEquipment':
                case 'ClientEquipment':
                case 'SiteEquipment':
                    types.forEach(type => {
                        url = `${url}&equipmentTypeId=${type}`;
                    });
                    break;
                case 'CompanySystem':
                case 'ClientSystem':
                case 'SiteSystem':
                    types.forEach(type => {
                        url = `${url}&systemTypeId=${type}`;
                    });
                    break;
                default:
                    break;
            }

        }

        if (!isEmpty(priorities)) {
            priorities.forEach(priority => {
                url = `${url}&priority=${priority}`;
            });
        }

        const response = await k2aBaseApi.get(
            url,
            header
        );

        meta = get(response, 'data.summaryMeta', {});
        const data = get(response, 'data.healthcheckSummaries', {});
        totalRecordCount = meta.totalRecordCount;

        let totalPages = totalRecordCount / 250;
        let count;
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        }
        else {
            count = totalPages + 1;
        }

        // fetching all equipment as a background process
        let promises = [];
        for (let i = 2; i <= count; i++) {
            options.page = i;
            let url = DashboardDataUrl.expand({ companyId, ...options });

            if (!isEmpty(types)) {
                switch (tabView) {
                    case 'CompanyClient':
                        types.forEach(type => {
                            url = `${url}&businessTypeId=${type}`;
                        });
                        break;
                    case 'CompanySite':
                    case 'ClientSite':
                        types.forEach(type => {
                            url = `${url}&buildingTypeId=${type}`;
                        });
                        break;
                    case 'CompanyEquipment':
                    case 'ClientEquipment':
                    case 'SiteEquipment':
                        types.forEach(type => {
                            url = `${url}&equipmentTypeId=${type}`;
                        });
                        break;
                    case 'CompanySystem':
                    case 'ClientSystem':
                    case 'SiteSystem':
                        types.forEach(type => {
                            url = `${url}&systemTypeId=${type}`;
                        });
                        break;
                    default:
                        break;
                }

            }

            if (!isEmpty(priorities)) {
                priorities.forEach(priority => {
                    url = `${url}&priority=${priority}`;
                });
            }
            promises.push(
                k2aBaseApi.get(
                    url,
                    header
                )
            );
        }

        let results = [];

        await axios.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.healthcheckSummaries);
                });
            }));

        totalPayload = data !== null ? data.concat(...results) : [];

        if (!isEmpty(totalPayload)) {
            totalPayload.forEach(entity => {
                const keys = Object.keys(entity).filter(item => item.toLowerCase().includes('score'));

                keys.forEach(key => {
                    if (entity[key]) {
                        entity[key] = entity[key] * 100;
                    }
                });

                entity.companyId = companyId;

                if (entity.areas) {
                    entity.areaList = orderBy(entity.areas, ['name'], ['asc']).map(a => a.name).join(', ');
                }
                else {
                    entity.areaList = null;
                }
            });
        }

        totalPayload = sortDashboardData(cloneDeep(totalPayload), tabView, getState().dashboard.dashboardIsCurrentTimeStamp && ignoreTimestamp.includes(tabView));
        
        switch (tabView) {
            case 'CompanyClient':

                dispatch({
                    type:    FETCH_CLIENTS_DATA,
                    payload: {
                        clients: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'CompanySite':

                dispatch({
                    type:    FETCH_SITES_DATA,
                    payload: {
                        sites: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'CompanyEquipment':

                dispatch({
                    type:    FETCH_EQUIPMENT_DATA,
                    payload: {
                        equipment: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'CompanySystem':

                dispatch({
                    type:    FETCH_SYSTEMS_DATA,
                    payload: {
                        systems: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'ClientSite':
                dispatch({
                    type:    FETCH_CLIENT_SITES_DATA,
                    payload: {
                        clientSites: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'ClientEquipment':
                dispatch({
                    type:    FETCH_CLIENT_EQUIPMENT_DATA,
                    payload: {
                        clientEquipment: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'ClientSystem':

                dispatch({
                    type:    FETCH_CLIENT_SYSTEMS_DATA,
                    payload: {
                        clientSystems: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'SiteEquipment':

                dispatch({
                    type:    FETCH_SITE_EQUIPMENT_DATA,
                    payload: {
                        siteEquipment: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'SiteSystem':

                dispatch({
                    type:    FETCH_SITE_SYSTEMS_DATA,
                    payload: {
                        siteSystems: [...totalPayload],
                        meta
                    }
                });
                break;
            case 'SystemEquipment':

                dispatch({
                    type:    FETCH_SYSTEM_EQUIPMENT_DATA,
                    payload: {
                        systemEquipment: [...totalPayload],
                        meta
                    }
                });
                break;
            default:
                break;
        }
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setDashboardCurrentTimestamp(moment().local()));
        dispatch(setBobLoading(tabView));
        dispatch(setProgressBarLoading(false));
    }
};

const sortDashboardData = (totalPayload, tabView, isCurrentTimeStamp) => {
    let sortField = [];
    let sortDirection = [];
    if (isCurrentTimeStamp || tabView === 'SiteEquipment' || tabView === 'SiteSystem' || tabView === 'SystemEquipment'
    ) {
        sortField = ['highFaultCount', 'mediumFaultCount', 'lowFaultCount'];
        sortDirection = ['desc', 'desc', 'desc'];
    }
    else {
        switch (tabView) {
            case 'CompanyClient':
                sortField.push(d => d.clientName.toLowerCase());
                sortDirection.push('asc');
                break;
            case 'CompanySite':
                sortField.push('siteScore');
                sortDirection.push('asc');
                break;
            case 'CompanyEquipment':
                sortField.push('equipmentScore');
                sortDirection.push('asc');
                break;
            case 'CompanySystem':
                sortField.push('systemScore');
                sortDirection.push('asc');
                break;
            case 'ClientSite':
                sortField.push('siteScore');
                sortDirection.push('asc');
                break;
            case 'ClientEquipment':
                sortField.push('equipmentScore');
                sortDirection.push('asc');
                break;
            case 'ClientSystem':
                sortField.push('systemScore');
                sortDirection.push('asc');
                break;
            case 'SiteEquipment':
                sortField.push('equipmentScore');
                sortDirection.push('asc');
                break;
            case 'SiteSystem':
                sortField.push('systemScore');
                sortDirection.push('asc');
                break;
            case 'SystemEquipment':
                sortField.push('highFaultCount');
                sortDirection.push('asc');
                break;
            default:
                sortField.push('highFaultCount');
                sortField.push('mediumFaultCount');
                sortField.push('lowFaultCount');
                sortDirection.push('desc');
                sortDirection.push('desc');
                sortDirection.push('desc');
                break;
        } 
    } 
    totalPayload = orderBy(totalPayload, sortField, sortDirection);

    return totalPayload;
};

export const clearDashboardData = () => async (dispatch, getState) => {
    dispatch({
        type: CLEAR_DASHBOARD_DATA
    });
};

export const clearDashboardClientData = () => async (dispatch, getState) => {
    dispatch({
        type: CLEAR_DASHBOARD_CLIENT_DATA
    });
};

export const clearDashboardSiteData = () => async (dispatch, getState) => {
    dispatch({
        type: CLEAR_DASHBOARD_SITE_DATA
    });
};

export const fetchEnergyKpiData = () => async (dispatch, getState) => {
    // Implement api consumption when api is ready.

    const payload = energyKpiData.data;

    dispatch({
        type: FETCH_ENERGY_KPI_DATA,
        payload
    });
};

export const fetchKpiData = (companyId, tabView, entityId, startTime, endTime, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.tabView = tabView;
    options.entityId = entityId;
    options.startDate = startTime;
    options.endDate = endTime;

    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        options.startDate = null;
        options.endDate = null;
    }

    dispatch(setProgressBarLoading(true));

    let payload = {};

    try {
        const response = await k2aBaseApi.get(
            DashboardKpiUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        payload = get(response, 'data', {});

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
    }

    switch (tabView) {
        case 'SiteEquipment':
            dispatch({
                type:    FETCH_SITE_EQUIPMENT_KPI_DATA,
                payload: { ...payload }
            });
            break;
        case 'SiteSystem':
            dispatch({
                type:    FETCH_SITE_SYSTEM_KPI_DATA,
                payload: { ...payload }
            });
            break;
        case 'Equipment':
            dispatch({
                type:    FETCH_EQUIPMENT_KPI_DATA,
                payload: { ...payload }
            });
            break;
        case 'System':
            dispatch({
                type:    FETCH_SYSTEM_KPI_DATA,
                payload: { ...payload }
            });
            break;
        default:
            break;
    }
};

export const fetchEnergyPrintIntegrations = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            EnergyPrintUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const data = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        dispatch({
            type:    FETCH_ENERGY_INTEGRATIONS,
            payload: {
                energyIntegrations: [...data],
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
    }
};
export const setClonedFormula = (options = {}) => async (dispatch, getState) => {
    dispatch({
        type:    SET_CLONED_FORMULA,
        payload: Math.floor(Date.now() / 1000)
    });
};

export const fetchSensorCalculations = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorCalculations'));
    options.limit = 250;
    options.page = 1;
    try {
        const response = await k2aBaseApi.get(
            SensorCalculationUrl.expand({
                ...options
            }),
            header
        );

        let data = get(response, 'data.data', []);
        if (data && data.length >0) {
            data = data.sort((a, b) => a.name.localeCompare(b.name));
        }
        dispatch({
            type:    FETCH_SENSOR_CALCULATIONS,
            payload: [...data]
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};

export const createTemplate = (body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    // header.headers['K2A-Company'] = '00000000-0000-0000-0000-000000000000';
    dispatch(setBobLoading('SensorCalculations'));
    try {
        await k2aBaseApi.post(
            SensorCalculationCreateUrl.expand({
                ...options
            }),
            body,
            header
        );
        dispatch(enqueueSnackbar('Template created successfully.', 'success'));
        dispatch({
            type:    SENSOR_SAVE_EVENT,
            payload: Math.floor(Date.now() / 1000)
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
    finally {
        dispatch(setBobLoading('SensorCalculations'));
        
    }
};

export const updateTemplate = (options = {},body) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('SensorCalculations'));
    try {
        await k2aBaseApi.put(
            SensorCalculationLoadUrl.expand({
                ...options
            }),
            body,
            header
        );
        dispatch(enqueueSnackbar('Template created successfully.', 'success'));
        dispatch({
            type:    SENSOR_SAVE_EVENT,
            payload: Math.floor(Date.now() / 1000)
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
    finally {
        dispatch(setBobLoading('SensorCalculations'));
        
    }
};

export const deleteTemplate = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('SensorCalculations'));
    try {
        await k2aBaseApi.delete(
            SensorCalculationLoadUrl.expand({
                ...options
            }),
            header
        );
        dispatch(enqueueSnackbar('Template deleted successfully.', 'success'));
        dispatch({
            type:    SENSOR_SAVE_EVENT,
            payload: Math.floor(Date.now() / 1000)
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
    finally {
        dispatch(setBobLoading('SensorCalculations'));
        
    }
};

export const createFormula = (body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('SensorCalculations'));
    try {
        await k2aBaseApi.post(
            formulaCreateUrl.expand({
                ...options
            }),
            body,
            header
        );
        dispatch(enqueueSnackbar('Formula created successfully.', 'success'));
        dispatch({
            type:    SENSOR_SAVE_EVENT,
            payload: Math.floor(Date.now() / 1000)
        });
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
    finally {
        dispatch(setBobLoading('SensorCalculations'));
        
    }
};

export const fetchFormulaList = (companyId,options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorCalculations'));

    try {
        const response = await k2aBaseApi.get(
            formulaListUrl.expand({
                companyId
            }),
            header
        );

        let data = get(response, 'data.data', []);
        if (data && data.length >0) {
            data = data.sort((a, b) => a.name.localeCompare(b.name));
        }
        dispatch({
            type:    FETCH_FORMULA_LIST,
            payload: [...data]
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};

export const fetchCompanyFormulaList = (companyId,options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorCalculations'));

    try {
        const response = await k2aBaseApi.get(
            formulaListUrl.expand({
                companyId
            }),
            header
        );

        let data = get(response, 'data.data', []);
        if (data && data.length >0) {
            data = data.sort((a, b) => a.name.localeCompare(b.name));
        }
        dispatch({
            type:    FETCH_COMPANY_FORMULA_LIST,
            payload: [...data]
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};

export const updateFormula = (options = {},body) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorCalculations'));

    try {
        
        await k2aBaseApi.put(
            formulaDetailsUrl.expand({
                ...options
            }),
            body,
            header
        );
        dispatch(enqueueSnackbar('Formula updated successfully.', 'success'));
        dispatch({
            type:    SENSOR_SAVE_EVENT,
            payload: Math.floor(Date.now() / 1000)
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};

export const deleteFormula = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorCalculations'));

    try {
        
        await k2aBaseApi.delete(
            formulaDetailsUrl.expand({
                ...options
            }),
            header
        );
        dispatch(enqueueSnackbar('Formula deleted successfully.', 'success'));
        dispatch({
            type:    SENSOR_SAVE_EVENT,
            payload: Math.floor(Date.now() / 1000)
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};


export const fetchFormulaDetails = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorCalculations'));

    try {
        const response = await k2aBaseApi.get(
            formulaDetailsUrl.expand({
                ...options
            }),
            header
        );

        const data = get(response, 'data', []);

        dispatch({
            type:    SET_FORMULA_DETAILS,
            payload: {...data}
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};

export const fetchTemplateDetails = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let clone = options.mode === 'clone'?true:false;
    delete options.mode;
    dispatch(setBobLoading('SensorCalculations'));

    try {
        const response = await k2aBaseApi.get(
            SensorCalculationLoadUrl.expand({
                ...options
            }),
            header
        );

        const data = get(response, 'data', []);
        data.mode =clone?'clone':null;
        dispatch({
            type:    SET_TEMPLATE_DETAILS,
            payload: {...data}
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
        dispatch(setBobLoading('SensorCalculations'));
    }
};

export const setNewFormula = (options = {}) => async (dispatch, getState) => {

    dispatch({
        type:    SET_FORMULA_DETAILS,
        payload: {name: '',template: ''}
    });

};

export const setNewTemplates = (options = {}) => async (dispatch, getState) => {

    dispatch({
        type:    SET_TEMPLATE_DETAILS,
        payload: {name: '',template: ''}
    });

};

export const updateEnergyPrintIntegration = (companyId, siteId, integrationId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const body = {
            companyId:               companyId,
            integrationsToBeUpdated: [
                {
                    integrationId: integrationId,
                    linkedSiteId:  siteId
                }
            ]
        };

        await k2aBaseApi.put(
            EnergyPrintUrl.expand({
                companyId,
                ...options
            }),
            body,
            header
        );

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
    }
};

export const fetchCompanyEnergyData = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.tabView = 'CompanyEnergyPrint';

    dispatch(setProgressBarLoading(true));

    let data = [];
    let meta = {};

    try {
        const response = await k2aBaseApi.get(
            DashboardDataUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        data = get(response, 'data.data', []);
        meta = get(response, 'data.meta', {});

        if (companyId === '896a8814-90e9-4ca1-bb46-66867fb1734e') {
            data.forEach(item => {
                item.energystarScore = Math.floor(Math.random() * (80 - 31) + 31);
            });
        }
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
    }

    dispatch({
        type:    FETCH_COMPANY_ENERGY_DATA,
        payload: {
            energySites: [...data],
            meta
        }
    });
};

export const fetchClientEnergyData = (companyId, clientId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    options.tabView = 'ClientEnergyPrint';
    options.clientId = clientId;

    dispatch(setProgressBarLoading(true));

    let data = [];
    let meta = {};

    try {
        const response = await k2aBaseApi.get(
            DashboardDataUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        data = get(response, 'data.data', []);
        meta = get(response, 'data.meta', {});

        if (companyId === '896a8814-90e9-4ca1-bb46-66867fb1734e') {
            data.forEach(item => {
                item.energystarScore = Math.floor(Math.random() * (80 - 31) + 31);
            });
        }
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setProgressBarLoading(false));
    }

    dispatch({
        type:    FETCH_CLIENT_ENERGY_DATA,
        payload: {
            energyClientSites: [...data],
            meta
        }
    });
};

export const fetchDashboardCompanyKpis = () => async (dispatch, getState) => {
    const payload = companyKpiData.data;

    dispatch({
        type: FETCH_COMPANY_KPI_DATA,
        payload
    });
};

export const fetchRuleScoreKpis = (companyId, entityId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('RuleKpi'));

    // dont pass timestamp when current is selected
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        options.To = null;
        options.From = null;
    }


    try {
        const response = await k2aBaseApi.get(
            RuleScoreKpiUrl.expand({
                companyId,
                entityId,
                ...options
            }),
            header
        );
        let payload = get(response, 'data', []);

        payload = orderBy(payload, ['score'], ['asc']);

        payload.forEach(rule => {
            if (rule.score !== null) {
                rule.score = rule.score * 100;
            }
        });

        dispatch({
            type: FETCH_RULE_SCORE_KPIS,
            payload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('RuleKpi'));
    }
};

export const setRuleScoreKpis = payload => async (dispatch, getState) => {
    dispatch({
        type: FETCH_RULE_SCORE_KPIS,
        payload
    });
};

export const setCompanyTabIndex = index => async (dispatch, getState) => {
    dispatch({
        type:    SET_COMPANY_TAB_INDEX,
        payload: index
    });
};

export const setClientTabIndex = index => async (dispatch, getState) => {
    dispatch({
        type:    SET_CLIENT_TAB_INDEX,
        payload: index
    });
};

export const setEquipmentTabIndex = index => async (dispatch, getState) => {
    dispatch({
        type:    SET_EQUIPMENT_TAB_INDEX,
        payload: index
    });
};


export const setSiteTabIndex = index => async (dispatch, getState) => {
    dispatch({
        type:    SET_SITE_TAB_INDEX,
        payload: index
    });
};

export const setSystemTabIndex = index => async (dispatch, getState) => {
    dispatch({
        type:    SET_SYSTEM_TAB_INDEX,
        payload: index
    });
};

export const setDashboardTimePeriod = period => async (dispatch, getState) => {
    dispatch({
        type:    SET_DASHBOARD_TIME_PERIOD,
        payload: period
    });
};

export const setDashboardTimeframeEnd = timestamp => async dispatch => {
    dispatch({
        type:    SET_DASHBOARD_TIMEFRAME_END,
        payload: timestamp
    });
};

export const setDashboardTimeframeStart = timestamp => async dispatch => {
    dispatch({
        type:    SET_DASHBOARD_TIMEFRAME_START,
        payload: timestamp
    });
};

export const setDashboardStartTimestamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_DASHBOARD_START_TIMESTAMP,
        payload: timestamp
    });
};

export const setDashboardIsCurrentTimestamp = isCurrentTimeStamp => async dispatch => {
    dispatch({
        type:    SET_DASHBOARD_ISCURRENT_TIMESTAMP,
        payload: isCurrentTimeStamp
    });
};

export const setDashboardCurrentTimestamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_DASHBOARD_CURRENT_TIMESTAMP,
        payload: timestamp
    });
};

export const setDashboardEndTimestamp = timestamp => async dispatch => {
    dispatch({
        type:    SET_DASHBOARD_END_TIMESTAMP,
        payload: timestamp
    });
};

export const setDesiredInterval = desiredInterval => async dispatch => {
    dispatch({
        type:    SET_DESIRED_INTERVAL,
        payload: desiredInterval
    });
};

export const exportGraphData = (view, sensorsList, payload, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let startTimeStamp = payload.startDate.format();
    let endTimeStamp = payload.endDate.format();
    const desiredInterval = options.desiredInterval || getState().dashboard.desiredInterval;

    // dont pass timestamp when current is selected
    // if (getState().dashboard.dashboardIsCurrentTimeStamp) {
    //     startTimeStamp = null;
    //     endTimeStamp = null;
    // }

    options.export = true;

    let url;

    sensorsList.forEach(sensor => {
        url = `${url}&sensorId=${sensor.id}`;
    });

    switch (view) {
        case 'sensorgraph':
        case 'sensorgrid':
            url = SensordataUrl.expand({
                page:            1,
                pageSize:        1000,
                desiredInterval: desiredInterval,
                endTimeStamp,
                startTimeStamp,
                ...options
            });
            sensorsList.forEach(sensor => {
                url = `${url}&sensorId=${sensor.id}`;
            });


            try {
                dispatch(enqueueSnackbar('Data export initialized', 'success'));

                const response = await lambdaBaseApi.get(
                    url,
                    header
                );

                const processId = get(response, 'data.data', '');

                dispatch(setProcessId(processId));

            } catch (error) {
                dispatch(handleNetworkError(error));
            }

            break;
        case 'scoregraph':
        case 'scoregrid':
            url = DashboardTimeSeriesDataUrl.expand({
                page:                               1,
                pageSize:                           3000,
                DesiredTimeSeriesIntervalInMinutes: desiredInterval,
                to:                                 endTimeStamp,
                from:                               startTimeStamp,
                companyId:                          payload.company,
                exportType:                         'SensorRuleScoreTimeSeries',
                ...options
            });
            getState().dashboard.selectedRuleSensors.forEach(sensor => {
                url = `${url}&sensorId=${sensor.id}`;
            });

            if (options.RuleId) {
                url = `${url}&RuleId=${options.RuleId}`;
            }
            else {
                url = `${url}&RuleId=${getState().dashboard.selectedRule.ruleId}`;
            }

            dispatch(enqueueSnackbar('Data export initialized', 'success'));

            try {
                const response = await k2aBaseApi.get(
                    url,
                    header
                );

                const processId = get(response, 'data.processId', '');

                dispatch(setProcessId(processId));

            } catch (error) {
                dispatch(handleNetworkError(error));
            }

            break;
        default:
            break;
    }

};

export const exportTableData = ( tabView, payload, tableProps, options = {} ) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.export = true;
    options.companyId = payload.company;
    options['sort'] = 'clientName^asc';
    options.startDate = payload.startDate ? payload.startDate.format() : null;
    options.endDate = payload.endDate ? payload.endDate.format() : null;

    // dont pass timestamp when current is selected
    // if (getState().dashboard.dashboardIsCurrentTimeStamp) {
    //     options.startDate = null;
    //     options.endDate = null;
    // }

    if (!isEmpty(tableProps)) {
        if (!isEmpty(tableProps.search)) {
            options.searchFilter = tableProps.search;
        }

        if (!isEmpty(tableProps.sort)) {
            options.sort = tableProps.sort;
        }
    }

    if (tabView === 'CompanySites') {
        options.tabView = 'CompanySite';
    } else if (tabView === 'CompanySystems') {
        options.tabView = 'CompanySystem';
    } else if (tabView === 'ClientSites') {
        options.tabView = 'ClientSite';
    } else if (tabView === 'ClientSystems') {
        options.tabView = 'ClientSystem';
    } else if (tabView === 'SiteSystems') {
        options.tabView = 'SiteSystem';
    } else {
        options.tabView = tabView;
    }

    if (payload.client) {
        options.clientId = payload.client;
    }
    if (payload.site) {
        options.siteId = payload.site;
    }
    if (payload.equipment) {
        options.equipmentId = payload.equipment;
    }
    if (payload.system) {
        options.systemId = payload.system;
    }

    dispatch(enqueueSnackbar('Data export initialized', 'success'));

    // export url for all the tabs is dashboard
    let url = DashboardDataUrl.expand({
        ...options
    });

    let filters;
    let priorities;

    switch (tabView) {
        case 'CompanyClients':
            filters = getState().dashboard.clientFilters;
            priorities = keys(pickBy(filters.priority));
            filters.type.forEach(type => {
                url = `${url}&businessTypeId=${type.id}`;
            });
            break;
        case 'CompanySites':
        case 'ClientSites':
            filters = getState().dashboard.siteFilters;
            priorities = keys(pickBy(filters.priority));
            filters.type.forEach(type => {
                url = `${url}&buildingTypeId=${type.id}`;
            });
            break;
        case 'CompanyEquipment':
        case 'ClientEquipment':
        case 'SiteEquipment':
            filters = getState().dashboard.equipmentFilters;
            priorities = keys(pickBy(filters.priority));
            filters.type.forEach(type => {
                url = `${url}&equipmentTypeId=${type.id}`;
            });
            break;
        case 'CompanySystems':
        case 'ClientSystems':
        case 'SiteSystems':
            filters = getState().dashboard.systemFilters;
            priorities = keys(pickBy(filters.priority));
            filters.type.forEach(type => {
                url = `${url}&systemTypeId=${type.id}`;
            });
            break;
        default:
            break;
    }

    if (!isEmpty(priorities)) {
        priorities.forEach(priority => {
            url = `${url}&priority=${priority}`;
        });
    }

    // equipment faults tab export url
    if (tabView === 'EquipmentFaults') {
        const StartTime = payload.startDate ? payload.startDate.format() : null;
        const EndTime = payload.endDate ? payload.endDate.format() : null;

        url = EquipmentFaultsDataExportUrl.expand({
            companyId:   options.companyId,
            equipmentId: options.equipmentId,
            export:      true,
            StartTime,
            EndTime
        });
    } else if (tabView === 'SystemFaults') {
        const StartTime = payload.startDate ? payload.startDate.format() : null;
        const EndTime = payload.endDate ? payload.endDate.format() : null;

        url = EquipmentFaultsDataExportUrl.expand({
            companyId:   options.companyId,
            equipmentId: options.systemId,
            export:      true,
            StartTime,
            EndTime
        });
    }

    try {
        const response = await k2aBaseApi.get(
            url,
            header
        );

        const processId = get(response, 'data.processId', '');

        dispatch(setProcessId(processId));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const exportTimeSeriesData = (tabView, payload, tableProps, options = {}) => async (dispatch, getState) => {
    // const ignoreTimestamp = ['SiteEquipment', 'SiteSystem'];
    const header = await awsHeader();
    const DesiredTimeSeriesIntervalInMinutes = getState().dashboard.desiredInterval;

    options.export = true;
    options.exportType = 'SensorTimeSeries';
    options.companyId = payload.company;

    // dont pass timestamp when current is selected
    // if (getState().dashboard.dashboardIsCurrentTimeStamp && ignoreTimestamp.includes(tabView)) {
    //     options.from = null;
    //     options.to = null;
    // }

    if (tabView === 'SiteSystems') {
        options.tabView = 'SiteSystem';
    } else {
        options.tabView = tabView;
    }

    if (payload.client) {
        options.clientId = payload.client;
    }
    if (payload.site) {
        options.siteId = payload.site;
    }

    if (!isEmpty(tableProps)) {
        if (!isEmpty(tableProps.search)) {
            options.searchFilter = tableProps.search;
        }

        if (!isEmpty(tableProps.sort)) {
            options.sort = tableProps.sort;
        }
    }

    // export url for all the tabs is dashboard
    let url = DashboardTimeSeriesDataUrl.expand({
        ...options,
        DesiredTimeSeriesIntervalInMinutes
    });

    let filters;
    let priorities;

    switch (tabView) {
        case 'SiteEquipment':
            filters = getState().dashboard.equipmentFilters;
            priorities = keys(pickBy(filters.priority));
            filters.type.forEach(type => {
                url = `${url}&equipmentTypeId=${type.id}`;
            });
            break;
        case 'SiteSystems':
            filters = getState().dashboard.systemFilters;
            priorities = keys(pickBy(filters.priority));
            filters.type.forEach(type => {
                url = `${url}&systemTypeId=${type.id}`;
            });
            break;
        default:
            break;
    }

    if (!isEmpty(priorities)) {
        priorities.forEach(priority => {
            url = `${url}&priority=${priority}`;
        });
    }

    dispatch(enqueueSnackbar('Data export initialized', 'success'));

    try {
        const response = await k2aBaseApi.get(
            url,
            header
        );

        const processId = get(response, 'data.processId', '');

        dispatch(setProcessId(processId));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchExportedFileUrl = (payload, isTimeSeriesExport = false) => async (dispatch, getState) => {
    const header = await awsHeader();

    if (payload.status === 'Complete') {
        const companyId = payload.companyId;
        const resourceType = isTimeSeriesExport ? 'SensorScoreDataExport' : payload.processType === 'CustomerSupportReportCreate' ? 'CustomerSupportReportDataExport' : 'DataExport';
        const fileType = isTimeSeriesExport ? 'xlsx' : 'csv';

        const resourceId = payload.result.exportFileKey;

        try {
            const response = await k2aBaseApi.get(
                ExportedFileUrl.expand({
                    companyId,
                    resourceId,
                    resourceType
                }),
                header
            );

            const fileUrl = response.data.resourceUrl;
            const fileName = resourceId + '.' + fileType;
            const element = document.createElement('a');
            element.href = fileUrl;
            element.download = `export.${fileType}`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            dispatch(enqueueSnackbar(`${fileName} file downloaded`, 'success'));
        } catch (error) {
            dispatch(handleNetworkError(error));
        }
    }

    dispatch(setIsShowExportModal(false));
};

export const fetchSiteEquipmentScores = (siteId, systems, equipmentTypes, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('EquipmentScores'));

    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        options.startDate = null;
        options.endDate = null;
    }

    try {
        let url = EquipmentScoreUrl.expand({ siteId, ...options });
        if (!url.includes('?')) {
            url += '?';
        }

        if (!isEmpty(systems)) {
            systems.forEach(system => {
                url = `${url}&systemIds=${system}`;
            });
        }

        if (!isEmpty(equipmentTypes)) {
            equipmentTypes.forEach(type => {
                url = `${url}&EquipmentType=${type}`;
            });
        }

        const response = await k2aBaseApi.get(url, header);
        let payload = get(response, 'data.data', '');

        payload = orderBy(payload, ['score'], ['asc']);

        payload.forEach(item => {
            if (item.score) item.score = item.score * 100;
        });

        dispatch(setBobLoading('EquipmentScores'));
        return payload;
    } catch (error) {
        dispatch(setBobLoading('EquipmentScores'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchSensorBySensorId = sensorId => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            SensorUrl.expand({
                sensorId
            }),
            header
        );

        const sensor = get(response, 'data', '');

        dispatch(setSelectedSensor(sensor));

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchTemplateSensorById = sensorId => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            SensorUrl.expand({
                sensorId
            }),
            header
        );

        const sensor = get(response, 'data', '');

        return sensor;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};


export const fetchSensorTemplate = (options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    let url = '/sensortemplates';

    let body = {
        id:          options.Id,
        companyId:   options.companyId,
        equipmentId: []
    };
    try {
        if (options.equipments) {
            options.equipments.forEach(type => {
                body.equipmentId.push(type);
            });
        }
        const response = await k2aBaseApi.post(
            url,
            body,
            header
        );
        const payload = get(response, 'data', '');

        dispatch({
            type: FETCH_SENSOR_TEMPLATE,
            payload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchConfigEntitesBySiteId = (siteId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    try {
        const systemResponse = k2aBaseApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );
        const equipmentResponse = k2aBaseApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        const data = [];
        let results = [];
        let promises = [systemResponse, equipmentResponse];
        let nextPromises = [];
        await axios.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach((r, idx) => {
                    results.push(r.data.data);
                    const totalCount = r.data.meta.totalCount;
                    const totalPages = totalCount / options.limit;
                    let count;
                    if (Number.isInteger(totalPages)) {
                        count = totalPages;
                    }
                    else {
                        count = totalPages + 1;
                    }
                    for (let i = 2; i < count; i++) {
                        if (idx === 0) {
                            // systems
                            options.page = i;
                            nextPromises.push(k2aBaseApi.get(
                                SystemsUrl.expand({
                                    siteId,
                                    ...options
                                }),
                                header
                            ));
                        }
                        else {
                            // equipment
                            options.page = i;
                            nextPromises.push(k2aBaseApi.get(
                                EquipmentUrl.expand({
                                    siteId,
                                    ...options
                                }),
                                header
                            ));
                        }
                    }
                });
            }));

        await axios.all(nextPromises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        const payload = data !== null ? data.concat(...results) : [];
        dispatch({
            type: FETCH_CONFIG_ENTITIES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};


export const setIsShowExportModal = isShow => async (dispatch, getState) => {
    dispatch({
        type:    SET_SHOW_EXPORT_MODAL,
        payload: isShow
    });
};

export const setSelectedSensor = sensor => async dispatch => {
    dispatch({
        type:    SET_SELECTED_SENSOR,
        payload: sensor
    });
};

export const setSelectedSensors = sensors => async dispatch => {
    dispatch({
        type:    SET_SELECTED_SENSORS,
        payload: sensors
    });
};

export const setSelectedRuleSensors = sensors => async dispatch => {
    dispatch({
        type:    SET_SELECTED_RULE_SENSORS,
        payload: sensors
    });
};

export const setSelectedRule = rule => async dispatch => {
    dispatch({
        type:    SET_SELECTED_RULE,
        payload: rule
    });
};

export const setSelectedWeatherSensors = sensors => async dispatch => {
    dispatch({
        type:    SET_SELECTED_WEATHER_SENSORS,
        payload: sensors
    });
};

export const setSelectedRuleFromFaultsPage = rule => async dispatch => {
    dispatch({
        type:    SET_SELECTED_RULE_FROM_FAULTS_PAGE,
        payload: rule
    });
};

export const setClientFilters = filters => async dispatch => {
    dispatch({
        type:    SET_CLIENT_FILTERS,
        payload: filters
    });
};

export const setEquipmentFilters = filters => async dispatch => {
    dispatch({
        type:    SET_EQUIPMENT_FILTERS,
        payload: filters
    });
};

export const setSiteFilters = filters => async dispatch => {
    dispatch({
        type:    SET_SITE_FILTERS,
        payload: filters
    });
};

export const setSystemFilters = filters => async dispatch => {
    dispatch({
        type:    SET_SYSTEM_FILTERS,
        payload: filters
    });
};

export const setHealthMonitorFilters = filters => async dispatch => {
    dispatch({
        type:    SET_HEALTH_MONITOR_FILTERS,
        payload: filters
    });
};

export const setSensorConfiguration = configuration => async (dispatch, getState) => {
    const payload = configuration;

    dispatch({
        type: SET_SENSOR_CONFIGURATION,
        payload
    });
};

export const setRuleScoringConfiguration = configuration => async (dispatch, getState) => {
    const payload = configuration;

    dispatch({
        type: SET_RULE_SCORING_CONFIGURATION,
        payload
    });
};

export const fetchSensorIds = (siteId, entityId, ruleIds) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('SensorIds'));
    let sensorIds;

    try {
        let url = SensorIdsUrl.expand({ siteId, entityId });

        ruleIds.forEach(id => {
            url = `${url}&RuleId=${id}`;
        });

        const response = await k2aBaseApi.get(url, header);

        const sensors = get(response, 'data', []);

        sensorIds = sensors.filter(s => s.id !== EMPTY_GUID);

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('SensorIds'));
    }

    return sensorIds;
};

export const createAnnouncement = (body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('Announcements'));
    try {
        await k2aBaseApi.post(
            AnnouncementUrl.expand({
                ...options
            }),
            body,
            header
        );
        dispatch(enqueueSnackbar('Announcement created successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
    finally {
        dispatch(setBobLoading('Announcements'));

    }
};

export const updateAnnouncement = (body, announcementId) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('Announcements'));
    try {
        await k2aBaseApi.put(
            AnnouncementUrlwithId.expand({
                announcementId
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Announcement updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
    finally {
        dispatch(setBobLoading('Announcements'));
    }
};

export const updateActiveAnnouncement = (body, announcementId) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('Announcements'));
    try {
        await k2aBaseApi.patch(
            AnnouncementUrlwithId.expand({
                announcementId
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Announcement updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
    finally {
        dispatch(setBobLoading('Announcements'));
    }
};

export const deleteAnnouncement = (announcementId) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('Announcements'));
    try {
        const response = await k2aBaseApi.delete(
            AnnouncementUrlwithId.expand({
                announcementId
            }),
            header
        );
        dispatch(enqueueSnackbar('Announcement deleted successfully.', 'success'));

        return response;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
    finally {
        dispatch(setBobLoading('Announcements'));
    }
};

export const deleteAnnouncements = (announcementIds) => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('Announcements'));
    try {
        for (let i=0;i<announcementIds.length;i++) {
            const announcementId = announcementIds[i];
            await k2aBaseApi.delete(
                AnnouncementUrlwithId.expand({
                    announcementId
                }),
                header
            );
        }
        dispatch(enqueueSnackbar('Announcements deleted successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
    finally {
        dispatch(setBobLoading('Announcements'));
    }
};



export const fetchAnnouncementsList = (admin) => async (dispatch, getState) => {

    const header = await awsHeader();
    dispatch(setBobLoading('Announcements'));
    const options = {
        limit: 1000,
        page:  1
    };
    let totalPayload = [];
    let totalCount = 0;

    try {
        const getPayload = async (page = 1) => {
            options.limit = 1000;
            options.page = page;

            let url = AnnouncementUrl.expand({...options});
            if (!admin) {
                // add the statuses
                url = `${url}&statuses=Active&statuses=inactive`;
            }

            const response = await k2aBaseApi.get(
                url,
                header
            );
            const meta = get(response, 'data.meta', '');
            const payload = get(response, 'data.data', '');
            totalCount = meta.totalCount;

            totalPayload = [...totalPayload, ...payload];

            page++;
            if (payload.length > 0 && totalPayload.length < totalCount) {
                return await getPayload(page);
            }
            return;
        };


        await getPayload();

        totalPayload.forEach(c => {
            c.announcementTypeLbl = c.announcementType === 'ReleaseNote' ? 'Release Notes' : '';
            c.createdBy = c.createdByUser ? c.createdByUser.firstName + ' ' + c.createdByUser.lastName:'';
            c.updatedBy = c.lastModifiedByUser ? c.lastModifiedByUser.firstName + ' ' + c.lastModifiedByUser.lastName:'';
            c.insertTimestamp = new Date(c.insertTimestamp);
            c.lastModifiedTimestamp = new Date(c.lastModifiedTimestamp);
            c.startDate = c.startDate ? new Date(c.startDate) : null;
            c.endDate = c.endDate ? new Date(c.endDate) : null;
        });
        totalPayload.sort((a, b) => {
            if (a.status < b.status) {
                return -1;
            }
            if (a.status > b.status) {
                return 1;
            }
            return 0;
        });

        dispatch({
            type:    FETCH_ANNOUNCEMENTS,
            payload: totalPayload
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('Announcements'));
    }
};

export const fetchAnnouncementMessages = () => async (dispatch, getState) => {
    try {
        dispatch(setBobLoading('Announcements'));
        const payload = announcementMsg.data;
        dispatch({
            type: FETCH_ANNOUNCEMENT_MSG,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('Announcements'));
    }
};

export const fetchInspectionReports = (companyId,options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = companyId;
    dispatch(setBobLoading('Reporting'));
    options.processType || (options.processType = 'InspectionReportCreate');

    try {

        const response = await k2aBaseApi.get(
            ReportsUrl.expand({
                ...options
            }),
            header
        );

        const payload = get(response, 'data', []);
        let results = [];

        payload.forEach(item => {
            results.push({
                clientName:  get(item, 'result.clientName', ''),
                reportData:  get(item, 'result.reportData', {}),
                processId:   item.processId,
                groupId:     item.processGroupId,
                siteName:    get(item, 'result.siteName', ''),
                status:      item.status,
                targetDate:  get(item, 'result.targetDateEnd', ''),
                targetStart: get(item, 'result.targetDateStart', ''),
                targetEnd:   get(item, 'result.targetDateEnd', ''),
                timestamp:   get(item, 'timestamp', '')
            });
        });

        results = orderBy(results, ['timestamp'], ['desc']);

        dispatch({
            type: FETCH_REPORT_LIST,
            results
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('Reporting'));
    }
};

export const createCustomerSupportReport = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = companyId;
    dispatch(setBobLoading('Reporting'));

    try {
        const response = await k2aBaseApi.post(
            CreateCustomerSupportReportUrl.expand({
                ...options
            }),
            null,
            header
        );

        const payload = get(response, 'data', '');

        dispatch(setProcessId(payload.processId));
        dispatch(enqueueSnackbar('Customer Support Report requested successfully.', 'success'));

        dispatch({
            type: CREATE_CUSTOMER_SUPPORT_REPORT,
            payload
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    } finally {
        dispatch(setBobLoading('Reporting'));
    }
};

export const createInspectionReport = (companyId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = companyId;
    dispatch(setBobLoading('Reporting'));

    try {
        const response = await lambdaBaseApi.post(
            CreateReportUrl.expand({
                ...options
            }),
            body,
            header
        );

        const payload = get(response, 'data.data', '');

        dispatch(enqueueSnackbar('Inspection Report requested successfully.', 'success'));

        dispatch({
            type: CREATE_INSPECTION_REPORT,
            payload
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    } finally {
        dispatch(setBobLoading('Reporting'));
    }
};

export const deleteInspectionReport = (processId, groupId, companyId,options = {}) => async dispatch => {
    const header = await awsHeader();
    header.headers['K2A-Company'] = companyId;
    dispatch(setBobLoading('Reporting'));

    try {
        await k2aBaseApi.delete(
            ReportUrl.expand({
                processId,
                groupId
            }),
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    } finally {
        dispatch(setBobLoading('Reporting'));
    }
};

export const setEmails = emails => async (dispatch, getState) => {
    let emailList = emails;

    dispatch({
        type:    SET_EMAILS,
        payload: emailList
    });
};

export const resetPassword = (userId) => async (dispatch) => {
    const header = await awsHeader();

    dispatch(setBobLoading('ResetPassword'));

    try {
        const body = [];

        await k2aBaseApi.post(
            ResetPasswordUrl.expand({ userId }),
            body,
            header
        );

        dispatch(enqueueSnackbar('Reset Password successful.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('ResetPassword'));
    }
};

export const setWorkBookMode = enabled => async (dispatch, getState) => {
    dispatch({
        type:    SET_WORKBOOK_MODE,
        payload: enabled
    });
};

export const fetchUserList = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = 20);
    options.page || (options.page = 1);

    dispatch(setBobLoading('FetchUserList'));

    try {
        const response = await k2aBaseApi.get(
            UsersUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const data = get(response, 'data.data', '');
        const meta = get(response, 'data.meta', {});
        const total = meta.totalCount;

        dispatch({
            type:    FETCH_USER_LIST,
            payload: {
                users: data,
                meta
            }
        });

        let totalPages = total / options.limit;
        let count;
        if (Number.isInteger(totalPages)) {
            count = totalPages;
        } else {
            count = totalPages + 1;
        }

        // fetching all rules as a background process
        let promises = [];
        for (let i = 2; i <= count; i++) {
            options.page = i;
            promises.push(
                lambdaBaseApi.get(
                    UsersUrl.expand({
                        companyId,
                        ...options
                    }),
                    header
                )
            );
        }

        let results = [];

        await axios.all(promises.map(p => p))
            .then(axios.spread((...result) => {
                result.forEach(r => {
                    results.push(r.data.data);
                });
            }));

        let allUsers = data.concat(...results);

        allUsers.forEach((user, index) => {
            user.userTypesList = [...new Set(user.userTypes.map(u => u.usertype))].join(', ').replace(/([A-Z])/g, ' $1').trim();
        });

        dispatch({
            type:    FETCH_USER_LIST,
            payload: {
                users: allUsers,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('FetchUserList'));
    }
};

export const createUser = (body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('CreateUser'));

    try {
        await k2aBaseApi.post(
            CreateUserUrl.expand({
                options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('User created successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('CreateUser'));
    }
};

export const updateUser = (userId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('UpdateUser'));

    try {
        await k2aBaseApi.put(
            UpdateUserUrl.expand({
                userId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('User updated successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('UpdateUser'));
    }
};

export const deleteUserType = userTypeUserId => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('DeleteUserType'));

    try {
        await k2aBaseApi.delete(
            DeleteUserTypeUrl.expand({
                userTypeUserId
            }),
            header
        );

        dispatch(enqueueSnackbar('User type Deleted successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('DeleteUserType'));
    }
};

export const addUserType = (userId, companyId, body) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('AddUserType'));

    try {
        const response =  await k2aBaseApi.post(
            AddUserTypeUrl.expand({
                userId,
                companyId
            }),
            body,
            header
        );
        
        dispatch(enqueueSnackbar('User type Added successfully.', 'success'));

        const data = get(response, 'data', '');
        return data.id;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('AddUserType'));
    }
};

export const fetchUserEntities = () => async (dispatch, getState) => {
    const payload = userEntitiesData.data;

    dispatch({
        type: FETCH_USER_ENTITIES,
        payload
    });
};

export const fetchUserApplications = (userId, companyId) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchUserApplications'));

    try {
        const response = await k2aBaseApi.get(
            FetchUserApplicationsUrl.expand({
                userId,
                companyId
            }),
            header
        );
        
        const data = get(response, 'data', '');
        return data;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('FetchUserApplications'));
    }
};

export const createUserCompanyApplication = body => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('CreateUserCompanyApplication'));

    try {
        await k2aBaseApi.post(
            CreateUserCompanyApplicationsUrl.expand({}),
            body,
            header
        );
    
        dispatch(enqueueSnackbar('User Applications Added successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('CreateUserCompanyApplication'));
    }
};

export const updateUserCompanyApplication = (userCompanyApplicationId, body) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('UpdateUserCompanyApplication'));

    try {
        await k2aBaseApi.patch(
            UpdateUserCompanyApplicationsUrl.expand({
                userCompanyApplicationId
            }),
            body,
            header
        );
    
        dispatch(enqueueSnackbar('User Applications Updated successfully.', 'success'));
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('UpdateUserCompanyApplication'));
    }
};

export const fetchUserContactDetails = (userId) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchUserContactDetails'));

    try {
        const response = await k2aBaseApi.get(
            FetchUserContactDetailsUrl.expand({
                userId,
                UserType: 'Contact'
                
            }),
            header
        );
        
        const data = get(response, 'data', '');
        return data;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('FetchUserContactDetails'));
    }
};

export const setFormulaGridColumns = payload => async (dispatch, getState) => {
    dispatch({
        type: SET_FORMULA_GRID_COLUMNS,
        payload
    });
};

export const setHealthscoreRawData = (data, meta) => async(dispatch, getState) => {
    dispatch({
        type:    FETCH_HEALTHSCORE_RAW_DATA,
        payload: {data, meta}
    });
};

export const fetchRawData = (companyId, equipmentId, systemId, ruleId, startDate, endDate) => async (dispatch, getState) => {
    const header = await awsHeader();
    if (getState().dashboard.dashboardIsCurrentTimeStamp) {
        startDate = null;
        endDate = null;
    }

    try {
        dispatch(setBobLoading('FetchHealthScoreRawData'));

        const response = await k2aBaseApi.get(
            DashboardDataUrl.expand({
                companyId,
                tabView: 'HealthScoreRawData',
                equipmentId,
                systemId,
                ruleId,
                startDate,
                endDate
            }),
            header
        );

        const meta = get(response, 'data.healthScoreRawDataMeta');
        const data = get(response, 'data.healthScoreRawDataSummaries');

        data.forEach(item => {
            item.start = new Date(item.effectiveTimestamp);
            item.end = item.expirationTimestamp === '2100-01-01T00:00:00Z' ? null : new Date(item.expirationTimestamp);
            item.score = item.score === 0 ? 0 : item.score ? item.score * 100 : null;
            if (item.fvovariables) {
                const fvoVariables = item.fvovariables.match(/([^[\]]+|\[\])/g).map( function(val) { return val==='[]' ? null : val; });
                fvoVariables.filter(v => v.trim() !== ',').forEach(fvo => {
                    const val = fvo.split(',');
                    item[`ruleFvoSensorName_${val[0].trim()}`]=getRuleFvoSensorFilterName(meta, ruleId, val[0].trim());
                    let value = val[1].trim();
                    if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
                        value = value.toLowerCase() === 'true';
                    }
                    else if (!isNaN(value)) {
                        value = toNumber(value);
                    }
                    item[`ruleFvoSensorVal_${val[0].trim()}`]=value;
                });
            }
            else {
                // build the columns from the meta
                if (!isEmpty(meta.info.rules)) {
                    meta.info.rules[0].sensorFilters.forEach(sf => {
                        item[`ruleFvoSensorName_${sf.id}`] = sf.sensorTypeName ? sf.sensorTypeName : 'Unknown Sensor Filter';
                        item[`ruleFvoSensorVal_${sf.id}`] = null;
                    });
                }
                
            }
        });

        const sortedData = orderBy(data, 'start', 'asc');
        dispatch({
            type:    FETCH_HEALTHSCORE_RAW_DATA,
            payload: {data: sortedData, meta}
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
    finally {
        dispatch(setBobLoading('FetchHealthScoreRawData'));
    }
};

export const storeQueryParamter = data => async (dispatch, getState) => {
    const header = await awsHeader();

    const parameters = { parameters: { ...data }};

    try {
        const response = await k2aBaseApi.post(
            QueryParameterUrl.expand({}), parameters, header
        );

        const payload = get(response, 'data', '');

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSiteFaultSummary = (companyId, siteId, startTime, endTime) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            SiteFaultsUrl.expand({
                companyId,
                siteId,
                startTime,
                endTime
            }),
            header
        );

        let data = get(response, 'data.siteRuleSummaries', []);

        data = orderBy(data, ['faultsCount'], ['desc']);

        data.forEach(item => {
            item.name = item.ruleName;
            item.priority = item.priority === 0 ? 'Low' : item.priority === 1 ? 'Medium' : 'High';
            item.issueTypes = item.issueTypes.toString();
            item.entities = orderBy(item.entities, ['faultsCount'], ['desc']);
            item.entities.forEach(entity => {
                entity.name = entity.entityName;
                entity.priority = entity.priority === 0 ? 'Low' : entity.priority === 1 ? 'Medium' : 'High';
                entity.currentStatus = entity.currentStatus === undefined ? '' : entity.currentStatus === 0 ? 'Ok' : entity.currentStatus === 1 ? 'In Fault' : entity.currentStatus === 2 ? 'Failure' : 'In Active';
            });
        });

        const meta = get(response, 'data.summaryMeta', {});

        dispatch({
            type:    SET_SITE_FAULT_SUMMARY,
            payload: {
                data,
                meta
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchSitesByCompanyId = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit = 1000;

    dispatch(setBobLoading('fetchSitesByCompanyId'));

    try {
        const response = await k2aBaseApi.get(
            SitesByCompanyIdUrl.expand({
                companyId,
                ...options
            }),
            header
        );
        
        const data = get(response, 'data.data', []);
        
        const groupedData = groupBy(data, 'clientName');
        let tempData = [];
        
        // process data for TreeList. name, id required
        Object.keys(groupedData).forEach(key => {
            tempData.push({
                name:     key,
                id:       groupedData[key][0].clientId,
                subItems: groupedData[key],
                isClient: true
            });
        });
        
        return tempData;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchSitesByCompanyId'));
        dispatch(setProgressBarLoading(false));
    }
};

export const fetchUserContextData = (userId, companyId) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchUserContextData'));
    
    try {
        const response = await k2aBaseApi.get(
            FetchUserContextUrl.expand({
                userId,
                companyId
            }),
            header
        );
                
        const data = get(response, 'data', []);

        data.clients.forEach(item => {
            item.subItems = [ ...item.sites ];
            item.isClient = true;
        });

        return data;
    } catch (error) {
        // dispatch(handleNetworkError(error));
        dispatch(handleNetworkError('No Scope'));
    } finally {
        dispatch(setBobLoading('fetchUserContextData'));
        dispatch(setProgressBarLoading(false));
    }
};

export const fetchRolesByUserId = (userId) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchRolesByUserId'));

    try {
        const response = await k2aBaseApi.get(
            RolesByUserIdUrl.expand({
                userId,
            }),
            header
        );
                
        const data = get(response, 'data', []);

        return data;
    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('fetchRolesByUserId'));
        dispatch(setProgressBarLoading(false));
    }
};
